import React from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import './MessageContent.css';

const MessageContent = ({ content }) => {
  // Function to parse message content and identify code blocks
  const parseContent = (text) => {
    const parts = [];
    let currentIndex = 0;
    
    // Regex to match code blocks with optional language specification
    // Supports both ```language and ``` formats
    const codeBlockRegex = /```(\w+)?\n?([\s\S]*?)```/g;
    
    let match;
    while ((match = codeBlockRegex.exec(text)) !== null) {
      // Add text before code block
      if (match.index > currentIndex) {
        const beforeText = text.slice(currentIndex, match.index);
        if (beforeText.trim()) {
          parts.push({
            type: 'text',
            content: beforeText,
            key: `text-${currentIndex}`
          });
        }
      }
      
      // Add code block
      const language = match[1] || 'text';
      const code = match[2].trim();
      
      parts.push({
        type: 'code',
        language: language.toLowerCase(),
        content: code,
        key: `code-${match.index}`
      });
      
      currentIndex = match.index + match[0].length;
    }
    
    // Add remaining text after last code block
    if (currentIndex < text.length) {
      const remainingText = text.slice(currentIndex);
      if (remainingText.trim()) {
        parts.push({
          type: 'text',
          content: remainingText,
          key: `text-${currentIndex}`
        });
      }
    }
    
    // If no code blocks found, return the entire text as a single text part
    if (parts.length === 0) {
      parts.push({
        type: 'text',
        content: text,
        key: 'text-0'
      });
    }
    
    return parts;
  };

  // Function to render text with @mentions highlighted
  const renderTextWithMentions = (text) => {
    const mentionRegex = /@(\w+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      
      // Add mention
      parts.push(
        <span key={`mention-${match.index}`} className="mention">
          @{match[1]}
        </span>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    
    return parts.length > 1 ? parts : text;
  };

  // Function to render inline code (single backticks)
  const renderTextWithInlineCode = (text) => {
    const inlineCodeRegex = /`([^`]+)`/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = inlineCodeRegex.exec(text)) !== null) {
      // Add text before inline code
      if (match.index > lastIndex) {
        const beforeText = text.slice(lastIndex, match.index);
        const mentionedText = renderTextWithMentions(beforeText);
        if (Array.isArray(mentionedText)) {
          parts.push(...mentionedText);
        } else {
          parts.push(mentionedText);
        }
      }
      
      // Add inline code
      parts.push(
        <code key={`inline-${match.index}`} className="inline-code">
          {match[1]}
        </code>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      const remainingText = text.slice(lastIndex);
      const mentionedText = renderTextWithMentions(remainingText);
      if (Array.isArray(mentionedText)) {
        parts.push(...mentionedText);
      } else {
        parts.push(mentionedText);
      }
    }
    
    return parts.length > 1 ? parts : renderTextWithMentions(text);
  };

  // Function to copy code to clipboard
  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      // You could add a toast notification here
      console.log('Code copied to clipboard');
    });
  };

  const contentParts = parseContent(content);

  return (
    <div className="message-content-wrapper">
      {contentParts.map((part) => {
        if (part.type === 'code') {
          return (
            <div key={part.key} className="code-block-wrapper">
              <div className="code-block-header">
                <span className="code-language">{part.language}</span>
                <button 
                  className="copy-button"
                  onClick={() => copyToClipboard(part.content)}
                  title="Copy code"
                >
                  📋
                </button>
              </div>
              <SyntaxHighlighter
                language={part.language}
                style={tomorrow}
                customStyle={{
                  margin: 0,
                  borderRadius: '0 0 6px 6px',
                  fontSize: '13px'
                }}
                wrapLongLines={true}
              >
                {part.content}
              </SyntaxHighlighter>
            </div>
          );
        } else {
          // Handle text with potential inline code
          const textWithInlineCode = renderTextWithInlineCode(part.content);
          return (
            <div key={part.key} className="text-content">
              {Array.isArray(textWithInlineCode) 
                ? textWithInlineCode.map((item, index) => 
                    typeof item === 'string' ? (
                      <span key={index}>{item}</span>
                    ) : item
                  )
                : textWithInlineCode
              }
            </div>
          );
        }
      })}
    </div>
  );
};

export default MessageContent;